import { Box, Grid, Paper, TextField, MenuItem, Button } from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker"; // make sure to install @mui/x-date-pickers
import { DataGrid } from "@mui/x-data-grid";
import { useEffect, useState } from "react";
import axiosClient from "../../api/axiosClient";
import Layout from "../../components/Layout";
import dayjs from "dayjs";
import { EmpShiftRequest } from "./EmpShiftRequest";
import { EmpShiftResponse } from "./EmpShiftResponse";
import { searchGridSx } from "../../utils/responsiveStyles";

export default function Shift() {
  // 🔹 Table state
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [rowCount, setRowCount] = useState(0);
  const [sortModel, setSortModel] = useState([]);

  // 🔹 Map frontend field names → backend DB names
  const orderByMap = {
    shiftId: "shift_id",
    endTime: "end_time",
    remarks: "remarks",
    shiftName: "shift_name",
    startTime: "start_time",
    userId: "user_id",
    configurationId: "configuration_id",
  };

  // 🔹 Dropdown options (from API)
  const [users, setUsers] = useState([]);

  // 🔹 Search filters
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [userId, setUserId] = useState(null);

  useEffect(() => {
    axiosClient
      .get("/shift/get-all-pts-user-lookup") // ✅ your backend endpoint for dropdowns
      .then((res) => {
        const data = res.data;
        console.log("device types: " + JSON.stringify(data));
        setUsers(data || []);
      })
      .catch((err) => console.error("Dropdown fetch failed", err));
  }, []);

  // 🔹 Fetch Data
  const fetchData = () => {
    const token = localStorage.getItem("token");
    console.log("token: " + token);
    setLoading(true);

    const request = new EmpShiftRequest();
    request.setStartIndex(page * pageSize);
    request.setLimit(pageSize);

    if (sortModel.length > 0) {
      const sort = sortModel[0]; // { field: "codeDescription", sort: "asc" }
      const backendField = orderByMap[sort.field] || sort.field;
      console.log(`order by ${backendField} ${sort.sort?.toUpperCase()}`);
      request.setOrderBy(
        `order by ${backendField} ${sort.sort?.toUpperCase()}`
      );
    }

    if (startDate) request.setStartDate(startDate.toISOString());
    if (endDate) request.setEndDate(endDate.toISOString());
    if (userId) request.setUserId(userId);

    axiosClient
      .post("/shift/get-all-emp-shift", request)
      .then((res) => {
        const ptsResponse = new EmpShiftResponse(res.data);
        if (
          ptsResponse?.getOperationResult() &&
          ptsResponse.getEmpShiftList()
        ) {
          setRows(ptsResponse.getEmpShiftList());
          setRowCount(ptsResponse.getCount());
        } else {
          setRows([]);
          setRowCount(0);
        }
      })
      .catch((err) => console.error(err))
      .finally(() => setLoading(false));
  };

  // initial load + refetch when page/pageSize changes
  useEffect(() => {
    console.log(page, pageSize);
    fetchData();
  }, [page, pageSize, sortModel]);

  // 🔹 Columns
  const columns = [
    { field: "shiftId", headerName: "Shift ID", width: 90 },
    {
      field: "userId",
      headerName: "Employee",
      flex: 1,
      renderCell: (params) => {
        const raw = params.row?.userId;
        const user = raw ? users.find((u) => u.id === raw) : null;
        return user ? user.name : "";
      },
    },
    {
      field: "startTime",
      headerName: "Start Time",
      flex: 1,
      renderCell: (params) => {
        const raw = params.row?.startTime;
        return raw ? dayjs(raw).format("DD/MM/YYYY HH:mm:ss") : "";
      },
    },
    {
      field: "end_time",
      headerName: "End Time",
      flex: 1,
      renderCell: (params) => {
        const raw = params.row?.endTime;
        return raw ? dayjs(raw).format("DD/MM/YYYY HH:mm:ss") : "";
      },
    },
  ];

  return (
    <Layout title="Employee Shift">
      {/* Search Form */}
      <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 3 }}>
        <Grid container sx={searchGridSx}>
          {/* search fields ... */}
          <Grid item xs={12} sm={6} md={3}>
            <DateTimePicker
              label="Start Date"
              value={startDate}
              onChange={(newVal) => setStartDate(newVal)}
              sx={{ width: "100%" }}
              renderInput={(params) => <TextField {...params} fullWidth />}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <DateTimePicker
              label="End Date"
              value={endDate}
              onChange={(newVal) => setEndDate(newVal)}
              sx={{ width: "100%" }}
              renderInput={(params) => <TextField {...params} fullWidth />}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              select
              label="User"
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              fullWidth
              sx={{ minWidth: { sm: 180 } }}
            >
              <MenuItem value="">All</MenuItem>
              {users.map((dt, i) => (
                <MenuItem key={i} value={dt.id}>
                  {dt.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Button
              variant="contained"
              color="primary"
              fullWidth
              sx={{ minWidth: { sm: 180 } }}
              onClick={fetchData}
            >
              Search
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* DataGrid */}
      <Box sx={{ width: "100%", overflowX: "auto" }}>
        <DataGrid
          rows={rows}
          columns={columns}
          getRowId={(row) => row.shiftId}
          pagination
          paginationMode="server"
          rowCount={rowCount}
          paginationModel={{ page, pageSize }}
          onPaginationModelChange={(model) => {
            setPage(model.page);
            setPageSize(model.pageSize);
          }}
          sortingMode="server"
          sortModel={sortModel}
          onSortModelChange={(model) => setSortModel(model)}
          sortingOrder={["asc", "desc"]}
          loading={loading}
          pageSizeOptions={[5, 10, 20, 50]}
          autoHeight
          sx={{ minWidth: { xs: 760, sm: "100%" }, bgcolor: "white" }}
        />
      </Box>
    </Layout>
  );
}
